import { useState, useMemo, useEffect } from 'react';
import {
  Building2, Users, FileText, DollarSign, Wrench, Search, Filter,
  ChevronDown, ChevronUp, Phone, Mail, MapPin, Calendar, AlertTriangle,
  CheckCircle, Clock, Home, Plus, X, User, Key, TrendingUp, AlertCircle,
  ArrowRight, Edit3, Eye, RefreshCw, Send, Bell, MessageSquare, RotateCcw
} from 'lucide-react';
import { theme } from '../../lib/theme';
import * as db from '../../lib/database';

// ============================================================
// MOCK DATA FOR PM MODULE (will be replaced by Supabase)
// ============================================================

const mockProperties = [
  {
    id: 1, address: '142 Oak Street', city: 'Cherry Hill', state: 'NJ', zip: '08002',
    property_type: 'multi_family', units: [
      { unit: 'Unit 1', tenant: 'Robert Barnes', rent: 1800, lease_end: '2026-08-31', status: 'occupied', paid_current: true },
      { unit: 'Unit 2', tenant: 'Lisa Chen', rent: 1650, lease_end: '2026-05-15', status: 'occupied', paid_current: true },
      { unit: 'Unit 3', tenant: 'Marcus Johnson', rent: 1750, lease_end: '2026-12-31', status: 'occupied', paid_current: false },
    ],
    owner: 'Frank Moretti', owner_phone: '856-555-0190', owner_email: 'frank.m@email.com',
    mgmt_fee_pct: 8, total_rent: 5200, open_maintenance: 1,
  },
  {
    id: 2, address: '85 Maple Ave', city: 'Haddonfield', state: 'NJ', zip: '08033',
    property_type: 'multi_family', units: [
      { unit: 'Unit 1', tenant: 'Jessica Torres', rent: 2100, lease_end: '2026-09-30', status: 'occupied', paid_current: true },
      { unit: 'Unit 2', tenant: null, rent: 2000, lease_end: null, status: 'vacant', paid_current: false },
    ],
    owner: 'Patricia Williams', owner_phone: '856-555-0222', owner_email: 'pwilliams@email.com',
    mgmt_fee_pct: 10, total_rent: 2100, open_maintenance: 1,
  },
  {
    id: 3, address: '302 Elm Street', city: 'Collingswood', state: 'NJ', zip: '08108',
    property_type: 'single_family', units: [
      { unit: null, tenant: 'Carlos Rivera', rent: 2400, lease_end: '2026-06-30', status: 'occupied', paid_current: true },
    ],
    owner: 'Howard Goldman', owner_phone: '609-555-0145', owner_email: 'hgoldman@email.com',
    mgmt_fee_pct: 8, total_rent: 2400, open_maintenance: 1,
  },
  {
    id: 4, address: '15 Cedar Lane', city: 'Voorhees', state: 'NJ', zip: '08043',
    property_type: 'multi_family', units: [
      { unit: 'Unit 1', tenant: 'Amy Patel', rent: 1550, lease_end: '2026-04-30', status: 'occupied', paid_current: true },
      { unit: 'Unit 2', tenant: 'Stephanie Brown', rent: 1600, lease_end: '2026-05-01', status: 'occupied', paid_current: true },
    ],
    owner: 'Frank Moretti', owner_phone: '856-555-0190', owner_email: 'frank.m@email.com',
    mgmt_fee_pct: 8, total_rent: 3150, open_maintenance: 0,
  },
  {
    id: 5, address: '220 White Horse Pike', city: 'Stratford', state: 'NJ', zip: '08084',
    property_type: 'commercial', units: [
      { unit: 'Suite A', tenant: 'NJ Dental Group', rent: 3500, lease_end: '2027-03-31', status: 'occupied', paid_current: true },
      { unit: 'Suite B', tenant: null, rent: 2800, lease_end: null, status: 'vacant', paid_current: false },
    ],
    owner: 'Sarah Investments LLC', owner_phone: '856-555-0300', owner_email: 'info@sarahinvest.com',
    mgmt_fee_pct: 10, total_rent: 3500, open_maintenance: 0,
  },
];

const mockLeases = [
  { id: 1, property: '15 Cedar Lane, Unit 1', tenant: 'Amy Patel', start: '2025-05-01', end: '2026-04-30', rent: 1550, status: 'expiring', deposit: 1550, renewal_status: 'pending' },
  { id: 2, property: '15 Cedar Lane, Unit 2', tenant: 'Stephanie Brown', start: '2025-05-01', end: '2026-05-01', rent: 1600, status: 'expiring', deposit: 1600, renewal_status: 'negotiating' },
  { id: 3, property: '85 Maple Ave, Unit 1', tenant: 'Jessica Torres', start: '2025-10-01', end: '2026-09-30', rent: 2100, status: 'active', deposit: 2100, renewal_status: null },
  { id: 4, property: '142 Oak Street, Unit 1', tenant: 'Robert Barnes', start: '2025-09-01', end: '2026-08-31', rent: 1800, status: 'active', deposit: 1800, renewal_status: null },
  { id: 5, property: '142 Oak Street, Unit 2', tenant: 'Lisa Chen', start: '2025-06-01', end: '2026-05-15', rent: 1650, status: 'expiring', deposit: 1650, renewal_status: 'offered' },
  { id: 6, property: '142 Oak Street, Unit 3', tenant: 'Marcus Johnson', start: '2026-01-01', end: '2026-12-31', rent: 1750, status: 'active', deposit: 1750, renewal_status: null },
  { id: 7, property: '302 Elm Street', tenant: 'Carlos Rivera', start: '2025-07-01', end: '2026-06-30', rent: 2400, status: 'expiring', deposit: 2400, renewal_status: 'pending' },
  { id: 8, property: '220 White Horse Pike, Suite A', tenant: 'NJ Dental Group', start: '2024-04-01', end: '2027-03-31', rent: 3500, status: 'active', deposit: 7000, renewal_status: null },
];

const mockRentRoll = [
  { property: '142 Oak Street, Unit 1', tenant: 'Robert Barnes', rent: 1800, april_status: 'paid', april_date: 'Apr 1', balance: 0 },
  { property: '142 Oak Street, Unit 2', tenant: 'Lisa Chen', rent: 1650, april_status: 'paid', april_date: 'Apr 2', balance: 0 },
  { property: '142 Oak Street, Unit 3', tenant: 'Marcus Johnson', rent: 1750, april_status: 'overdue', april_date: null, balance: 1750 },
  { property: '85 Maple Ave, Unit 1', tenant: 'Jessica Torres', rent: 2100, april_status: 'paid', april_date: 'Apr 1', balance: 0 },
  { property: '85 Maple Ave, Unit 2', tenant: null, rent: 2000, april_status: 'vacant', april_date: null, balance: 0 },
  { property: '302 Elm Street', tenant: 'Carlos Rivera', rent: 2400, april_status: 'paid', april_date: 'Apr 3', balance: 0 },
  { property: '15 Cedar Lane, Unit 1', tenant: 'Amy Patel', rent: 1550, april_status: 'paid', april_date: 'Apr 1', balance: 0 },
  { property: '15 Cedar Lane, Unit 2', tenant: 'Stephanie Brown', rent: 1600, april_status: 'paid', april_date: 'Apr 1', balance: 0 },
  { property: '220 White Horse Pike, Suite A', tenant: 'NJ Dental Group', rent: 3500, april_status: 'paid', april_date: 'Apr 1', balance: 0 },
  { property: '220 White Horse Pike, Suite B', tenant: null, rent: 2800, april_status: 'vacant', april_date: null, balance: 0 },
];

// ============================================================
// SUB-TABS
// ============================================================

const PM_TABS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'properties', label: 'Properties', icon: Building2 },
  { id: 'leases', label: 'Leases', icon: FileText },
  { id: 'renewals', label: 'Renewals', icon: RotateCcw },
  { id: 'rent', label: 'Rent Roll', icon: DollarSign },
];

// ============================================================
// MAIN PM VIEW
// ============================================================

export default function PMView({ demoMode, showToast }) {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProperty, setExpandedProperty] = useState(null);

  // Live data state
  const [liveProperties, setLiveProperties] = useState(null);
  const [liveLeases, setLiveLeases] = useState(null);
  const [liveRentRoll, setLiveRentRoll] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load live data from Supabase
  useEffect(() => {
    if (!demoMode) {
      loadPMData();
    }
  }, [demoMode]);

  const loadPMData = async () => {
    try {
      const [propsRes, leasesRes, statsRes, rentRes] = await Promise.all([
        db.getProperties({ is_managed: true }),
        db.getLeases(),
        db.getPMStats(),
        db.getRentRoll(new Date().getMonth() + 1, new Date().getFullYear()),
      ]);

      if (propsRes.data) setLiveProperties(propsRes.data);
      if (leasesRes.data) setLiveLeases(leasesRes.data);
      if (statsRes) setLiveStats(statsRes);
      if (rentRes.data) setLiveRentRoll(rentRes.data);
      setDataLoaded(true);
    } catch (err) {
      console.error('Error loading PM data:', err);
      setDataLoaded(true); // Fall back to mock
    }
  };

  const handleToast = (msg, isError) => {
    if (showToast) showToast(msg, isError);
  };

  // Use live data or fall back to mock
  const useProperties = (liveProperties && liveProperties.length > 0) ? liveProperties : mockProperties;
  const useLeases = (liveLeases && liveLeases.length > 0) ? liveLeases : mockLeases;
  const useRentRoll = (liveRentRoll && liveRentRoll.length > 0) ? liveRentRoll : mockRentRoll;

  // Computed stats — use live stats if available, otherwise compute from mock data
  const stats = useMemo(() => {
    if (liveStats && liveStats.totalUnits > 0) {
      // Enrich with rent roll data
      const overdue = useRentRoll.filter(r => r.status === 'overdue' || r.april_status === 'overdue').length;
      const overdueAmount = useRentRoll
        .filter(r => r.status === 'overdue' || r.april_status === 'overdue')
        .reduce((sum, r) => sum + (r.balance || 0), 0);
      return { ...liveStats, overdue, overdueAmount };
    }

    // Fall back to mock computation
    const totalUnits = mockProperties.reduce((sum, p) => sum + p.units.length, 0);
    const occupiedUnits = mockProperties.reduce((sum, p) => sum + p.units.filter(u => u.status === 'occupied').length, 0);
    const vacantUnits = totalUnits - occupiedUnits;
    const totalRent = mockProperties.reduce((sum, p) => sum + p.total_rent, 0);
    const totalMgmtFee = mockProperties.reduce((sum, p) => sum + (p.total_rent * p.mgmt_fee_pct / 100), 0);
    const expiringLeases = mockLeases.filter(l => l.status === 'expiring').length;
    const overdue = mockRentRoll.filter(r => r.april_status === 'overdue').length;
    const overdueAmount = mockRentRoll.filter(r => r.april_status === 'overdue').reduce((sum, r) => sum + r.balance, 0);
    const openMaint = mockProperties.reduce((sum, p) => sum + p.open_maintenance, 0);

    return { totalUnits, occupiedUnits, vacantUnits, totalRent, totalMgmtFee, expiringLeases, overdue, overdueAmount, openMaint };
  }, [liveStats, liveRentRoll]);

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
          Property Management
        </h1>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '4px 0 0' }}>
          {mockProperties.length} properties &middot; {stats.totalUnits} units &middot; {stats.occupiedUnits} occupied
        </p>
      </div>

      {/* Sub-tabs */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20,
        borderBottom: `2px solid ${theme.colors.border}`, paddingBottom: 0,
        overflowX: 'auto',
      }}>
        {PM_TABS.map(tab => {
          const active = activeSubTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px',
                border: 'none', cursor: 'pointer',
                backgroundColor: 'transparent',
                color: active ? theme.colors.primary : theme.colors.textLight,
                fontSize: 13, fontWeight: active ? 600 : 400,
                borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                marginBottom: -2,
                whiteSpace: 'nowrap',
              }}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeSubTab === 'overview' && <PMOverview stats={stats} properties={useProperties} />}
      {activeSubTab === 'properties' && (
        <PropertiesTab
          properties={useProperties}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          expandedProperty={expandedProperty}
          setExpandedProperty={setExpandedProperty}
          isLive={liveProperties && liveProperties.length > 0}
        />
      )}
      {activeSubTab === 'leases' && <LeasesTab leases={useLeases} isLive={liveLeases && liveLeases.length > 0} />}
      {activeSubTab === 'renewals' && (
        <LeaseRenewalsTab
          leases={useLeases}
          isLive={liveLeases && liveLeases.length > 0}
          demoMode={demoMode}
          onRefresh={loadPMData}
          showToast={handleToast}
        />
      )}
      {activeSubTab === 'rent' && <RentRollTab rentRoll={useRentRoll} stats={stats} isLive={liveRentRoll && liveRentRoll.length > 0} />}
    </div>
  );
}

// ============================================================
// PM OVERVIEW
// ============================================================

function PMOverview({ stats, properties = mockProperties }) {
  const attentionItems = [
    ...(stats.overdue > 0 ? [{ title: `${stats.overdue} rent payment${stats.overdue > 1 ? 's' : ''} overdue`, subtitle: `$${stats.overdueAmount.toLocaleString()} outstanding`, badge: 'OVERDUE', color: theme.colors.danger }] : []),
    ...(stats.expiringLeases > 0 ? [{ title: `${stats.expiringLeases} lease${stats.expiringLeases > 1 ? 's' : ''} expiring within 60 days`, subtitle: 'Review renewal options', badge: 'EXPIRING', color: theme.colors.warning }] : []),
    ...(stats.vacantUnits > 0 ? [{ title: `${stats.vacantUnits} vacant unit${stats.vacantUnits > 1 ? 's' : ''}`, subtitle: 'Potential revenue loss', badge: 'VACANT', color: theme.colors.accent }] : []),
    ...(stats.openMaint > 0 ? [{ title: `${stats.openMaint} open maintenance request${stats.openMaint > 1 ? 's' : ''}`, subtitle: 'Needs attention', badge: 'MAINT', color: theme.colors.info }] : []),
  ];

  return (
    <div>
      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Units" value={stats.totalUnits} icon={Building2} color={theme.colors.primary} />
        <StatCard label="Occupied" value={stats.occupiedUnits} icon={CheckCircle} color={theme.colors.success} />
        <StatCard label="Vacant" value={stats.vacantUnits} icon={Key} color={stats.vacantUnits > 0 ? theme.colors.warning : theme.colors.success} />
        <StatCard label="Monthly Rent" value={`$${stats.totalRent.toLocaleString()}`} icon={DollarSign} color={theme.colors.primary} />
        <StatCard label="Mgmt Fees" value={`$${Math.round(stats.totalMgmtFee).toLocaleString()}`} icon={TrendingUp} color={theme.colors.accent} />
        <StatCard label="Expiring Leases" value={stats.expiringLeases} icon={Calendar} color={stats.expiringLeases > 0 ? theme.colors.warning : theme.colors.success} />
      </div>

      {/* Occupancy Rate */}
      <div style={{
        background: theme.colors.card, borderRadius: theme.radius.md, padding: 16,
        boxShadow: theme.shadow.sm, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Occupancy Rate</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.primary }}>
            {Math.round((stats.occupiedUnits / stats.totalUnits) * 100)}%
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: theme.colors.border }}>
          <div style={{
            height: 8, borderRadius: 4,
            background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
            width: `${(stats.occupiedUnits / stats.totalUnits) * 100}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <span style={{ fontSize: 11, color: theme.colors.textLight }}>{stats.occupiedUnits} occupied</span>
          <span style={{ fontSize: 11, color: theme.colors.textLight }}>{stats.vacantUnits} vacant</span>
        </div>
      </div>

      {/* Attention Items */}
      {attentionItems.length > 0 && (
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 16,
          boxShadow: theme.shadow.sm, marginBottom: 16,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px' }}>
            Needs Attention
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attentionItems.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: theme.radius.sm,
                border: `1px solid ${theme.colors.border}`,
              }}>
                <AlertCircle size={16} color={item.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: theme.colors.textLight }}>{item.subtitle}</div>
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '3px 8px',
                  borderRadius: 10, color: 'white', background: item.color,
                }}>{item.badge}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Property Summary */}
      <div style={{
        background: theme.colors.card, borderRadius: theme.radius.md, padding: 16,
        boxShadow: theme.shadow.sm,
      }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, margin: '0 0 12px' }}>
          Properties at a Glance
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {properties.map(p => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: p.property_type === 'commercial'
                  ? `${theme.colors.accent}15` : `${theme.colors.primary}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Building2 size={16} color={p.property_type === 'commercial' ? theme.colors.accent : theme.colors.primary} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text }}>{p.address}</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                  {p.city} &middot; {p.units.length} unit{p.units.length > 1 ? 's' : ''} &middot; Owner: {p.owner}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                  ${p.total_rent.toLocaleString()}/mo
                </div>
                <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                  {p.units.filter(u => u.status === 'occupied').length}/{p.units.length} occupied
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PROPERTIES TAB
// ============================================================

function PropertiesTab({ properties, searchTerm, setSearchTerm, expandedProperty, setExpandedProperty }) {
  const filtered = properties.filter(p =>
    !searchTerm ||
    p.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: theme.colors.card, borderRadius: theme.radius.sm,
        padding: '8px 12px', border: `1px solid ${theme.colors.border}`,
        marginBottom: 16,
      }}>
        <Search size={16} color={theme.colors.textLight} />
        <input
          placeholder="Search properties, owners..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            border: 'none', outline: 'none', flex: 1,
            fontSize: 13, color: theme.colors.text, background: 'transparent',
          }}
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
            <X size={14} color={theme.colors.textLight} />
          </button>
        )}
      </div>

      {/* Property Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.map(property => {
          const isExpanded = expandedProperty === property.id;
          const occupiedCount = property.units.filter(u => u.status === 'occupied').length;
          const vacantCount = property.units.length - occupiedCount;

          return (
            <div key={property.id} style={{
              background: theme.colors.card, borderRadius: theme.radius.md,
              boxShadow: theme.shadow.sm, overflow: 'hidden',
              border: `1px solid ${isExpanded ? theme.colors.primary + '40' : theme.colors.border}`,
            }}>
              {/* Header */}
              <div
                onClick={() => setExpandedProperty(isExpanded ? null : property.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px', cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: property.property_type === 'commercial'
                    ? `${theme.colors.accent}15` : `${theme.colors.primary}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Building2 size={18} color={property.property_type === 'commercial' ? theme.colors.accent : theme.colors.primary} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                    {property.address}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                    {property.city}, {property.state} {property.zip}
                  </div>
                </div>
                <div style={{ textAlign: 'right', marginRight: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                    ${property.total_rent.toLocaleString()}<span style={{ fontSize: 11, fontWeight: 400, color: theme.colors.textLight }}>/mo</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 2 }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: `${theme.colors.success}15`, color: theme.colors.success, fontWeight: 600 }}>
                      {occupiedCount} occ
                    </span>
                    {vacantCount > 0 && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: `${theme.colors.warning}15`, color: theme.colors.warning, fontWeight: 600 }}>
                        {vacantCount} vac
                      </span>
                    )}
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={16} color={theme.colors.textLight} /> : <ChevronDown size={16} color={theme.colors.textLight} />}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${theme.colors.border}`, padding: 16 }}>
                  {/* Owner Info */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: theme.radius.sm,
                    background: `${theme.colors.primary}08`, marginBottom: 12,
                  }}>
                    <User size={14} color={theme.colors.primary} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text }}>Owner: {property.owner}</div>
                      <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                        Mgmt fee: {property.mgmt_fee_pct}% &middot; Fee: ${Math.round(property.total_rent * property.mgmt_fee_pct / 100)}/mo
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...iconBtnStyle, background: `${theme.colors.primary}15` }}>
                        <Phone size={13} color={theme.colors.primary} />
                      </button>
                      <button style={{ ...iconBtnStyle, background: `${theme.colors.info}15` }}>
                        <Mail size={13} color={theme.colors.info} />
                      </button>
                    </div>
                  </div>

                  {/* Units Table */}
                  <h4 style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, margin: '0 0 8px' }}>
                    Units ({property.units.length})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {property.units.map((unit, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.border}`,
                        background: unit.status === 'vacant' ? `${theme.colors.warning}06` : 'transparent',
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 6,
                          background: unit.status === 'occupied' ? `${theme.colors.success}15` : `${theme.colors.warning}15`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {unit.status === 'occupied'
                            ? <User size={13} color={theme.colors.success} />
                            : <Key size={13} color={theme.colors.warning} />
                          }
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: theme.colors.text }}>
                            {unit.unit || 'Whole property'} — {unit.tenant || 'VACANT'}
                          </div>
                          {unit.tenant && (
                            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                              Lease ends: {formatDate(unit.lease_end)}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                            ${unit.rent.toLocaleString()}
                          </div>
                          {unit.tenant && (
                            <span style={{
                              fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8,
                              background: unit.paid_current ? `${theme.colors.success}15` : `${theme.colors.danger}15`,
                              color: unit.paid_current ? theme.colors.success : theme.colors.danger,
                            }}>
                              {unit.paid_current ? 'PAID' : 'OVERDUE'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Property Stats */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12,
                  }}>
                    <div style={{ textAlign: 'center', padding: 8, borderRadius: theme.radius.sm, background: `${theme.colors.primary}08` }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.primary }}>${property.total_rent.toLocaleString()}</div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight }}>Total Rent</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 8, borderRadius: theme.radius.sm, background: `${theme.colors.accent}08` }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.accent }}>${Math.round(property.total_rent * property.mgmt_fee_pct / 100)}</div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight }}>Mgmt Fee</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 8, borderRadius: theme.radius.sm, background: property.open_maintenance > 0 ? `${theme.colors.warning}08` : `${theme.colors.success}08` }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: property.open_maintenance > 0 ? theme.colors.warning : theme.colors.success }}>{property.open_maintenance}</div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight }}>Open Maint</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// LEASES TAB
// ============================================================

function LeasesTab({ leases, isLive }) {
  const [filter, setFilter] = useState('all');

  const filtered = leases.filter(l => {
    if (filter === 'all') return true;
    // Support both mock format (status) and live format (renewal_status)
    const leaseStatus = l.status || (isLive ? getLeaseStatus(l) : 'active');
    return leaseStatus === filter;
  });

  function getLeaseStatus(lease) {
    const daysUntil = Math.ceil((new Date(lease.lease_end) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 60 && !['renewed', 'expired'].includes(lease.renewal_status)) return 'expiring';
    if (['renewed', 'expired'].includes(lease.renewal_status)) return lease.renewal_status;
    return 'active';
  }

  const renewalStatusColors = {
    pending: theme.colors.warning,
    negotiating: theme.colors.info,
    offered: theme.colors.primary,
    renewed: theme.colors.success,
  };

  return (
    <div>
      {/* Lease Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
        <button onClick={() => setFilter('all')} style={filterBtnStyle(filter === 'all')}>
          All ({leases.length})
        </button>
        <button onClick={() => setFilter('active')} style={filterBtnStyle(filter === 'active')}>
          Active ({leases.filter(l => (l.status || (isLive ? getLeaseStatus(l) : 'active')) === 'active').length})
        </button>
        <button onClick={() => setFilter('expiring')} style={filterBtnStyle(filter === 'expiring')}>
          Expiring ({leases.filter(l => (l.status || (isLive ? getLeaseStatus(l) : 'active')) === 'expiring').length})
        </button>
      </div>

      {/* Lease Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map(lease => {
          const leaseEndDate = lease.end || lease.lease_end;
          const daysUntilEnd = Math.ceil((new Date(leaseEndDate) - new Date()) / (1000 * 60 * 60 * 24));
          const isUrgent = daysUntilEnd <= 30;

          const leaseStatus = lease.status || (isLive ? getLeaseStatus(lease) : 'active');
          const propertyLabel = typeof lease.property === 'string'
            ? lease.property
            : lease.property
              ? `${lease.property.address}${lease.property.unit ? ', ' + lease.property.unit : ''}`
              : 'Unknown';
          const tenantLabel = typeof lease.tenant === 'string'
            ? lease.tenant
            : lease.tenant
              ? `${lease.tenant.first_name} ${lease.tenant.last_name}`
              : 'Unknown';
          const leaseRent = lease.rent || lease.monthly_rent || 0;
          const leaseDeposit = lease.deposit || lease.security_deposit || 0;
          const leaseStart = lease.start || lease.lease_start;
          const leaseEnd = lease.end || lease.lease_end;
          const renewalStatus = lease.renewal_status;

          return (
            <div key={lease.id} style={{
              background: theme.colors.card, borderRadius: theme.radius.md,
              padding: '14px 16px', boxShadow: theme.shadow.sm,
              borderLeft: `3px solid ${leaseStatus === 'expiring' ? (isUrgent ? theme.colors.danger : theme.colors.warning) : theme.colors.success}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                    {propertyLabel}
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>
                    <User size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {tenantLabel}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                      <Calendar size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                      {formatDate(leaseStart)} — {formatDate(leaseEnd)}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.text }}>
                      ${Number(leaseRent).toLocaleString()}/mo
                    </div>
                    <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                      Deposit: ${Number(leaseDeposit).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {leaseStatus === 'expiring' && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10,
                      background: isUrgent ? `${theme.colors.danger}15` : `${theme.colors.warning}15`,
                      color: isUrgent ? theme.colors.danger : theme.colors.warning,
                    }}>
                      {daysUntilEnd} days
                    </span>
                  )}
                  {leaseStatus === 'active' && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                      background: `${theme.colors.success}15`, color: theme.colors.success,
                    }}>
                      Active
                    </span>
                  )}
                  {renewalStatus && !['active', 'renewed', 'expired'].includes(renewalStatus) && (
                    <div style={{ marginTop: 4 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8,
                        background: `${renewalStatusColors[renewalStatus] || theme.colors.textLight}15`,
                        color: renewalStatusColors[renewalStatus] || theme.colors.textLight,
                        textTransform: 'uppercase',
                      }}>
                        {renewalStatus}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// RENT ROLL TAB
// ============================================================

function RentRollTab({ rentRoll, stats, isLive }) {
  // Normalize status field — mock uses april_status, live uses status
  const getStatus = (r) => r.status || r.april_status || 'unknown';
  const totalExpected = rentRoll.filter(r => getStatus(r) !== 'vacant').reduce((sum, r) => sum + r.rent, 0);
  const totalCollected = rentRoll.filter(r => getStatus(r) === 'paid').reduce((sum, r) => sum + r.rent, 0);
  const collectionRate = totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;

  const statusColors = {
    paid: theme.colors.success,
    overdue: theme.colors.danger,
    vacant: theme.colors.textLight,
    partial: theme.colors.warning,
  };

  const statusLabels = {
    paid: 'PAID',
    overdue: 'OVERDUE',
    vacant: 'VACANT',
    partial: 'PARTIAL',
  };

  return (
    <div>
      {/* Collection Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12, marginBottom: 16,
      }}>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.primary }}>
            ${totalCollected.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>Collected (April)</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text }}>
            ${totalExpected.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>Expected</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: stats.overdue > 0 ? theme.colors.danger : theme.colors.success }}>
            ${stats.overdueAmount.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>Outstanding</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: collectionRate >= 95 ? theme.colors.success : theme.colors.warning }}>
            {collectionRate}%
          </div>
          <div style={{ fontSize: 11, color: theme.colors.textLight }}>Collection Rate</div>
        </div>
      </div>

      {/* Rent Roll Table */}
      <div style={{
        background: theme.colors.card, borderRadius: theme.radius.md,
        boxShadow: theme.shadow.sm, overflow: 'hidden',
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 80px 70px 70px',
          padding: '10px 14px', background: `${theme.colors.primary}08`,
          borderBottom: `1px solid ${theme.colors.border}`,
          fontSize: 11, fontWeight: 600, color: theme.colors.textLight,
          gap: 8,
        }}>
          <span>Property</span>
          <span>Tenant</span>
          <span style={{ textAlign: 'right' }}>Rent</span>
          <span style={{ textAlign: 'center' }}>Status</span>
          <span style={{ textAlign: 'right' }}>Balance</span>
        </div>

        {/* Rows */}
        {rentRoll.map((row, idx) => {
          const rowStatus = getStatus(row);
          return (
            <div key={idx} style={{
              display: 'grid', gridTemplateColumns: '2fr 1.5fr 80px 70px 70px',
              padding: '10px 14px',
              borderBottom: idx < rentRoll.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
              fontSize: 12, color: theme.colors.text,
              background: rowStatus === 'overdue' ? `${theme.colors.danger}05` : 'transparent',
              gap: 8,
              alignItems: 'center',
            }}>
              <span style={{ fontWeight: 500, fontSize: 11 }}>{row.property}</span>
              <span style={{ color: row.tenant ? theme.colors.text : theme.colors.textLight, fontSize: 11 }}>
                {row.tenant || '— vacant —'}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 500 }}>${row.rent.toLocaleString()}</span>
              <span style={{ textAlign: 'center' }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 8,
                  background: `${statusColors[rowStatus]}15`,
                  color: statusColors[rowStatus],
                }}>
                  {statusLabels[rowStatus]}
                </span>
              </span>
              <span style={{
                textAlign: 'right', fontWeight: row.balance > 0 ? 600 : 400,
                color: row.balance > 0 ? theme.colors.danger : theme.colors.textLight,
              }}>
                {row.balance > 0 ? `$${row.balance.toLocaleString()}` : '—'}
              </span>
            </div>
          );
        })}

        {/* Total Row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 80px 70px 70px',
          padding: '12px 14px',
          borderTop: `2px solid ${theme.colors.border}`,
          fontSize: 12, fontWeight: 700, color: theme.colors.text, gap: 8,
        }}>
          <span>Total</span>
          <span>{rentRoll.filter(r => r.tenant).length} tenants</span>
          <span style={{ textAlign: 'right' }}>${rentRoll.reduce((s, r) => s + r.rent, 0).toLocaleString()}</span>
          <span></span>
          <span style={{ textAlign: 'right', color: stats.overdueAmount > 0 ? theme.colors.danger : theme.colors.success }}>
            ${stats.overdueAmount.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LEASE RENEWALS TAB — Automated 90/60/30 Day Reminders
// ============================================================

function LeaseRenewalsTab({ leases, isLive, demoMode, onRefresh, showToast }) {
  const [filter, setFilter] = useState('all');
  const [runningCheck, setRunningCheck] = useState(false);
  const [renewalActions, setRenewalActions] = useState([]);
  const [showRenewalModal, setShowRenewalModal] = useState(null); // lease id
  const [proposedRent, setProposedRent] = useState('');
  const [sendingSMS, setSendingSMS] = useState(null); // lease id

  // Import SMS functions dynamically to avoid circular deps
  const sendRenewalSMS = async (lease, reminderType) => {
    try {
      const smsLib = await import('../../lib/sms.js');
      const tenant = lease.tenant || { first_name: 'Tenant', last_name: '', phone: null };
      const property = lease.property || lease.propertyLabel || 'Property';
      const result = await smsLib.sendLeaseRenewalReminder(tenant, property, lease, reminderType);
      return result;
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Compute expiring leases with enriched data
  const renewalLeases = useMemo(() => {
    return leases
      .map(lease => {
        const leaseEnd = lease.end || lease.lease_end;
        if (!leaseEnd) return null;
        const daysUntilExpiry = Math.ceil((new Date(leaseEnd) - new Date()) / (1000 * 60 * 60 * 24));
        // Only show leases expiring within 120 days or recently expired
        if (daysUntilExpiry > 120 || daysUntilExpiry < -30) return null;
        // Skip already renewed/expired unless recently
        const status = lease.renewal_status;
        if (['renewed', 'expired'].includes(status) && daysUntilExpiry > 0) return null;

        let urgency = 'normal';
        let urgencyColor = theme.colors.success;
        let urgencyLabel = 'On Track';
        if (daysUntilExpiry <= 0) {
          urgency = 'expired';
          urgencyColor = theme.colors.danger;
          urgencyLabel = 'EXPIRED';
        } else if (daysUntilExpiry <= 30) {
          urgency = 'critical';
          urgencyColor = theme.colors.danger;
          urgencyLabel = `${daysUntilExpiry} days`;
        } else if (daysUntilExpiry <= 60) {
          urgency = 'warning';
          urgencyColor = theme.colors.warning;
          urgencyLabel = `${daysUntilExpiry} days`;
        } else if (daysUntilExpiry <= 90) {
          urgency = 'upcoming';
          urgencyColor = theme.colors.info;
          urgencyLabel = `${daysUntilExpiry} days`;
        } else {
          urgencyLabel = `${daysUntilExpiry} days`;
        }

        const propertyLabel = typeof lease.property === 'string'
          ? lease.property
          : lease.property
            ? `${lease.property.address}${lease.property.unit ? ', ' + lease.property.unit : ''}`
            : 'Unknown';
        const tenantLabel = typeof lease.tenant === 'string'
          ? lease.tenant
          : lease.tenant
            ? `${lease.tenant.first_name} ${lease.tenant.last_name}`
            : 'Unknown';
        const leaseRent = lease.rent || lease.monthly_rent || 0;

        return {
          ...lease,
          daysUntilExpiry,
          urgency,
          urgencyColor,
          urgencyLabel,
          propertyLabel,
          tenantLabel,
          leaseRent: Number(leaseRent),
          leaseEnd,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  }, [leases]);

  const filtered = renewalLeases.filter(l => {
    if (filter === 'all') return true;
    return l.urgency === filter;
  });

  const counts = {
    all: renewalLeases.length,
    critical: renewalLeases.filter(l => l.urgency === 'critical').length,
    warning: renewalLeases.filter(l => l.urgency === 'warning').length,
    upcoming: renewalLeases.filter(l => l.urgency === 'upcoming').length,
  };

  // Run automated renewal check
  const handleRunCheck = async () => {
    if (demoMode) {
      // Demo mode simulation
      setRenewalActions([
        { tenant: 'Amy Patel', property: '15 Cedar Lane, Unit 1', days_until_expiry: 21, action: 'Advanced to reminder_30' },
        { tenant: 'Stephanie Brown', property: '15 Cedar Lane, Unit 2', days_until_expiry: 22, action: 'Advanced to reminder_30' },
        { tenant: 'Lisa Chen', property: '142 Oak Street, Unit 2', days_until_expiry: 36, action: 'Advanced to reminder_60' },
      ]);
      if (showToast) showToast('Renewal check complete! 3 leases updated.');
      return;
    }

    setRunningCheck(true);
    try {
      const { actions, error } = await db.runLeaseRenewalCheck();
      if (error) {
        if (showToast) showToast('Error running renewal check', true);
      } else {
        setRenewalActions(actions);
        if (actions.length > 0) {
          if (showToast) showToast(`Renewal check complete! ${actions.length} lease(s) updated.`);
          if (onRefresh) await onRefresh();
        } else {
          if (showToast) showToast('All leases are up to date — no new reminders needed.');
        }
      }
    } catch (err) {
      if (showToast) showToast('Error: ' + err.message, true);
    }
    setRunningCheck(false);
  };

  // Send renewal offer
  const handleSendOffer = async (lease) => {
    const rent = parseFloat(proposedRent);
    if (!rent || rent <= 0) {
      if (showToast) showToast('Enter a valid proposed rent amount', true);
      return;
    }

    if (demoMode) {
      if (showToast) showToast(`Renewal offer sent to ${lease.tenantLabel}! Proposed: $${rent}/mo`);
      setShowRenewalModal(null);
      setProposedRent('');
      return;
    }

    try {
      const { error } = await db.sendLeaseRenewalOffer(lease.id, rent);
      if (error) {
        if (showToast) showToast('Error sending renewal offer', true);
      } else {
        if (showToast) showToast(`Renewal offer sent! ${lease.tenantLabel}: $${rent}/mo`);
        setShowRenewalModal(null);
        setProposedRent('');
        if (onRefresh) await onRefresh();
      }
    } catch (err) {
      if (showToast) showToast('Error: ' + err.message, true);
    }
  };

  // Send SMS reminder
  const handleSendSMS = async (lease) => {
    setSendingSMS(lease.id);
    let reminderType = '60day';
    if (lease.daysUntilExpiry <= 30) reminderType = '30day';
    else if (lease.daysUntilExpiry <= 60) reminderType = '60day';
    else reminderType = '90day';

    if (demoMode) {
      setTimeout(() => {
        if (showToast) showToast(`SMS reminder sent to ${lease.tenantLabel}! (Demo)`);
        setSendingSMS(null);
      }, 500);
      return;
    }

    const result = await sendRenewalSMS(lease, reminderType);
    if (result.success) {
      if (showToast) showToast(`SMS sent to ${lease.tenantLabel}`);
    } else {
      if (showToast) showToast(`SMS failed: ${result.error}`, true);
    }
    setSendingSMS(null);
  };

  // Renewal status display helpers
  const renewalStatusDisplay = {
    active: { label: 'Active', color: theme.colors.success },
    reminder_90: { label: '90-Day Sent', color: theme.colors.info },
    reminder_60: { label: '60-Day Sent', color: theme.colors.warning },
    reminder_30: { label: '30-Day Sent', color: theme.colors.danger },
    renewal_sent: { label: 'Offer Sent', color: theme.colors.primary },
    negotiating: { label: 'Negotiating', color: theme.colors.accent },
    renewed: { label: 'Renewed', color: theme.colors.success },
    not_renewing: { label: 'Not Renewing', color: theme.colors.danger },
    pending: { label: 'Pending', color: theme.colors.warning },
    offered: { label: 'Offered', color: theme.colors.primary },
    month_to_month: { label: 'Month-to-Month', color: theme.colors.info },
  };

  return (
    <div>
      {/* Header with Run Check button */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
            Lease Renewal Automation
          </h3>
          <p style={{ fontSize: 12, color: theme.colors.textLight, margin: '2px 0 0' }}>
            Automated 90/60/30 day reminders for expiring leases
          </p>
        </div>
        <button
          onClick={handleRunCheck}
          disabled={runningCheck}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: theme.radius.sm,
            background: runningCheck ? theme.colors.border : theme.colors.primary,
            color: 'white', border: 'none', cursor: runningCheck ? 'not-allowed' : 'pointer',
            fontSize: 12, fontWeight: 600,
          }}
        >
          <RefreshCw size={14} style={{ animation: runningCheck ? 'spin 1s linear infinite' : 'none' }} />
          {runningCheck ? 'Checking...' : 'Run Renewal Check'}
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
          border: `2px solid ${counts.critical > 0 ? theme.colors.danger + '40' : theme.colors.border}`,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: counts.critical > 0 ? theme.colors.danger : theme.colors.text }}>
            {counts.critical}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Critical (&lt;30 days)</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
          border: `2px solid ${counts.warning > 0 ? theme.colors.warning + '40' : theme.colors.border}`,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: counts.warning > 0 ? theme.colors.warning : theme.colors.text }}>
            {counts.warning}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Warning (30-60 days)</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.info }}>
            {counts.upcoming}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Upcoming (60-90 days)</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text }}>
            {counts.all}
          </div>
          <div style={{ fontSize: 10, color: theme.colors.textLight, fontWeight: 500 }}>Total Expiring</div>
        </div>
      </div>

      {/* Recent Actions from Check */}
      {renewalActions.length > 0 && (
        <div style={{
          background: `${theme.colors.success}08`, borderRadius: theme.radius.md,
          padding: 14, marginBottom: 16,
          border: `1px solid ${theme.colors.success}30`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.success, marginBottom: 8 }}>
            <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Last Check Results — {renewalActions.length} action(s) taken
          </div>
          {renewalActions.map((action, i) => (
            <div key={i} style={{ fontSize: 11, color: theme.colors.text, marginBottom: 2 }}>
              {action.tenant} at {action.property} — {action.action} ({action.days_until_expiry} days left)
            </div>
          ))}
        </div>
      )}

      {/* Filter Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setFilter('all')} style={filterBtnStyle(filter === 'all')}>All ({counts.all})</button>
        <button onClick={() => setFilter('critical')} style={filterBtnStyle(filter === 'critical')}>Critical ({counts.critical})</button>
        <button onClick={() => setFilter('warning')} style={filterBtnStyle(filter === 'warning')}>Warning ({counts.warning})</button>
        <button onClick={() => setFilter('upcoming')} style={filterBtnStyle(filter === 'upcoming')}>Upcoming ({counts.upcoming})</button>
      </div>

      {/* Lease Renewal Cards */}
      {filtered.length === 0 ? (
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md,
          padding: 40, textAlign: 'center', boxShadow: theme.shadow.sm,
        }}>
          <CheckCircle size={32} color={theme.colors.success} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
            {filter === 'all' ? 'No leases expiring in the next 120 days' : `No ${filter} leases`}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
            All leases are in good standing
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(lease => {
            const statusInfo = renewalStatusDisplay[lease.renewal_status] || renewalStatusDisplay.active;

            return (
              <div key={lease.id} style={{
                background: theme.colors.card, borderRadius: theme.radius.md,
                padding: '14px 16px', boxShadow: theme.shadow.sm,
                borderLeft: `4px solid ${lease.urgencyColor}`,
              }}>
                {/* Top Row: Property + Urgency */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                      {lease.propertyLabel}
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 2 }}>
                      <User size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                      {lease.tenantLabel}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                      background: `${lease.urgencyColor}15`, color: lease.urgencyColor,
                    }}>
                      {lease.urgencyLabel}
                    </span>
                    <div style={{ marginTop: 4 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8,
                        background: `${statusInfo.color}15`, color: statusInfo.color,
                        textTransform: 'uppercase',
                      }}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Row */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                    <Calendar size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                    Expires: {formatDate(lease.leaseEnd)}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.text }}>
                    ${lease.leaseRent.toLocaleString()}/mo
                  </div>
                  {lease.renewal_rent && (
                    <div style={{ fontSize: 11, color: theme.colors.primary, fontWeight: 600 }}>
                      Proposed: ${Number(lease.renewal_rent).toLocaleString()}/mo
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {/* Send SMS Reminder */}
                  {lease.daysUntilExpiry > 0 && !['renewed', 'not_renewing'].includes(lease.renewal_status) && (
                    <button
                      onClick={() => handleSendSMS(lease)}
                      disabled={sendingSMS === lease.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.border}`,
                        background: theme.colors.card, cursor: 'pointer',
                        fontSize: 11, fontWeight: 500, color: theme.colors.text,
                      }}
                    >
                      <MessageSquare size={12} />
                      {sendingSMS === lease.id ? 'Sending...' : 'SMS Reminder'}
                    </button>
                  )}

                  {/* Send Renewal Offer */}
                  {!['renewal_sent', 'renewed', 'not_renewing', 'negotiating'].includes(lease.renewal_status) && (
                    <button
                      onClick={() => {
                        setShowRenewalModal(lease.id);
                        setProposedRent(String(Math.round(lease.leaseRent * 1.03))); // Default 3% increase
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', borderRadius: theme.radius.sm,
                        border: 'none', background: theme.colors.primary,
                        cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'white',
                      }}
                    >
                      <Send size={12} />
                      Send Renewal Offer
                    </button>
                  )}

                  {/* Mark Renewed */}
                  {['renewal_sent', 'negotiating'].includes(lease.renewal_status) && (
                    <button
                      onClick={async () => {
                        if (demoMode) {
                          if (showToast) showToast('Marked as renewed! (Demo)');
                          return;
                        }
                        await db.updateLeaseRenewalResponse(lease.id, 'accepted');
                        if (showToast) showToast(`${lease.tenantLabel} renewed!`);
                        if (onRefresh) await onRefresh();
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', borderRadius: theme.radius.sm,
                        border: 'none', background: theme.colors.success,
                        cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'white',
                      }}
                    >
                      <CheckCircle size={12} />
                      Mark Renewed
                    </button>
                  )}

                  {/* Mark Not Renewing */}
                  {!['renewed', 'not_renewing'].includes(lease.renewal_status) && (
                    <button
                      onClick={async () => {
                        if (demoMode) {
                          if (showToast) showToast('Marked as not renewing (Demo)');
                          return;
                        }
                        await db.updateLeaseRenewalResponse(lease.id, 'declined');
                        if (showToast) showToast(`${lease.tenantLabel} — not renewing`);
                        if (onRefresh) await onRefresh();
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.danger}40`,
                        background: 'transparent', cursor: 'pointer',
                        fontSize: 11, fontWeight: 500, color: theme.colors.danger,
                      }}
                    >
                      <X size={12} />
                      Not Renewing
                    </button>
                  )}
                </div>

                {/* Renewal Offer Modal (inline) */}
                {showRenewalModal === lease.id && (
                  <div style={{
                    marginTop: 10, padding: 12, borderRadius: theme.radius.sm,
                    background: `${theme.colors.primary}08`,
                    border: `1px solid ${theme.colors.primary}30`,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 8 }}>
                      Send Renewal Offer to {lease.tenantLabel}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 11, color: theme.colors.textLight }}>Current: ${lease.leaseRent.toLocaleString()}/mo</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 11, color: theme.colors.text }}>New rent: $</span>
                        <input
                          type="number"
                          value={proposedRent}
                          onChange={e => setProposedRent(e.target.value)}
                          style={{
                            width: 90, padding: '4px 8px', borderRadius: 4,
                            border: `1px solid ${theme.colors.border}`,
                            fontSize: 12, outline: 'none', background: 'white',
                          }}
                        />
                        <span style={{ fontSize: 11, color: theme.colors.textLight }}>/mo</span>
                      </div>
                      {proposedRent && lease.leaseRent > 0 && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 8,
                          background: parseFloat(proposedRent) > lease.leaseRent ? `${theme.colors.success}15` : `${theme.colors.info}15`,
                          color: parseFloat(proposedRent) > lease.leaseRent ? theme.colors.success : theme.colors.info,
                        }}>
                          {parseFloat(proposedRent) > lease.leaseRent
                            ? `+${((parseFloat(proposedRent) / lease.leaseRent - 1) * 100).toFixed(1)}%`
                            : parseFloat(proposedRent) < lease.leaseRent
                              ? `${((parseFloat(proposedRent) / lease.leaseRent - 1) * 100).toFixed(1)}%`
                              : 'No change'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        onClick={() => handleSendOffer(lease)}
                        style={{
                          padding: '6px 14px', borderRadius: theme.radius.sm,
                          border: 'none', background: theme.colors.primary,
                          color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        Send Offer
                      </button>
                      <button
                        onClick={() => { setShowRenewalModal(null); setProposedRent(''); }}
                        style={{
                          padding: '6px 14px', borderRadius: theme.radius.sm,
                          border: `1px solid ${theme.colors.border}`,
                          background: 'transparent', color: theme.colors.textLight,
                          fontSize: 11, cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// HELPERS
// ============================================================

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div style={{
      background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
      boxShadow: theme.shadow.sm, display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.colors.text }}>{value}</div>
        <div style={{ fontSize: 10, color: theme.colors.textLight }}>{label}</div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const iconBtnStyle = {
  width: 28, height: 28, borderRadius: 7,
  border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function filterBtnStyle(active) {
  return {
    padding: '8px 14px', borderRadius: theme.radius.sm,
    border: active ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
    background: active ? `${theme.colors.primary}10` : theme.colors.card,
    color: active ? theme.colors.primary : theme.colors.textLight,
    fontWeight: active ? 600 : 400, fontSize: 12,
    cursor: 'pointer', textAlign: 'center',
  };
}
